import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/* ── tiny icons ─────────────────────────────────────────────────────────── */
function IconShield() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <polyline points="9 12 11 14 15 10"/>
    </svg>
  );
}
function IconEye({ off }) {
  return off ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function IconLoader() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
      <style>{`svg{animation:spin .8s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </svg>
  );
}

/* ── reusable field ─────────────────────────────────────────────────────── */
function Field({ label, type = 'text', value, onChange, placeholder, autoFocus }) {
  const [show, setShow] = useState(false);
  const isPass = type === 'password';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={isPass && show ? 'text' : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          autoFocus={autoFocus}
          style={{
            width: '100%',
            background: 'var(--bg-input)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            color: 'var(--text-primary)',
            fontSize: 14,
            padding: isPass ? '10px 40px 10px 12px' : '10px 12px',
            outline: 'none',
            transition: 'border-color .15s',
            fontFamily: 'var(--font-sans)',
          }}
          onFocus={e => (e.target.style.borderColor = 'var(--accent-blue)')}
          onBlur={e  => (e.target.style.borderColor = 'var(--border)')}
        />
        {isPass && (
          <button
            type="button"
            onClick={() => setShow(s => !s)}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', color: 'var(--text-muted)',
              display: 'flex', alignItems: 'center', cursor: 'pointer', padding: 0,
            }}
            tabIndex={-1}
          >
            <IconEye off={show} />
          </button>
        )}
      </div>
    </div>
  );
}

/* ── main ───────────────────────────────────────────────────────────────── */
export default function Login() {
  const { login, setup, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // 'login' | 'setup'
  const [mode, setMode] = useState('login');

  // login fields
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // setup fields
  const [sUsername, setSUsername] = useState('');
  const [sPassword, setSPassword] = useState('');
  const [sPasswordConfirm, setSPasswordConfirm] = useState('');
  const [sEmail,    setSEmail]    = useState('');
  const [sFullname, setSFullname] = useState('');

  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [busy,    setBusy]    = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  /* ── login submit ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Username dan password wajib diisi.');
      return;
    }
    setError(''); setBusy(true);
    try {
      const res = await login(username, password);
      if (res?.needsSetup) {
        setMode('setup');
        setError('');
        setSuccess('Belum ada akun admin. Buat akun pertama dulu.');
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /* ── setup submit ── */
  const handleSetup = async (e) => {
    e.preventDefault();
    if (!sUsername.trim() || !sPassword.trim() || !sPasswordConfirm.trim() || !sEmail.trim() || !sFullname.trim()) {
      setError('Semua field wajib diisi.');
      return;
    }
    if (sPassword.length < 8) {
      setError('Password minimal 8 karakter.');
      return;
    }
    if (sPassword !== sPasswordConfirm) {
      setError('Konfirmasi password tidak cocok.');
      return;
    }
    setError(''); setBusy(true);
    try {
      await setup({ username: sUsername, password: sPassword, email: sEmail, fullname: sFullname });
      setSuccess('Akun admin berhasil dibuat! Silakan login.');
      setMode('login');
      setUsername(sUsername);
      setPassword('');
      setSPassword('');
      setSPasswordConfirm('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const isBusy = busy || loading;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 24,
      fontFamily: 'var(--font-sans)',
    }}>
      {/* subtle grid bg */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
        backgroundSize: '48px 48px',
        opacity: 0.35,
      }} />

      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: 400,
        animation: 'fadeIn .3s ease-out',
      }}>
        {/* logo/brand */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 56, height: 56, borderRadius: 16,
            background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-cyan))',
            color: '#fff', marginBottom: 16,
            boxShadow: '0 0 32px rgba(59,130,246,0.3)',
          }}>
            <IconShield />
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
            RADIUS<span style={{ color: 'var(--accent-blue)' }}>MGT</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            {mode === 'login' ? 'Management Console' : 'Initial Setup'}
          </div>
        </div>

        {/* card */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          padding: 32,
          boxShadow: 'var(--shadow)',
        }}>
          {/* tab header for login */}
          {mode === 'login' && (
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Masuk</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                Masukkan kredensial admin Anda.
              </p>
            </div>
          )}

          {mode === 'setup' && (
            <div style={{ marginBottom: 24 }}>
              <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Buat Akun Admin</h2>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                Tidak ada akun admin yang ditemukan. Buat yang pertama untuk melanjutkan.
              </p>
            </div>
          )}

          {/* success / error banners */}
          {success && (
            <div style={{
              background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)',
              borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 20,
              fontSize: 13, color: 'var(--accent-green)',
            }}>
              {success}
            </div>
          )}
          {error && (
            <div style={{
              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 20,
              fontSize: 13, color: 'var(--accent-red)',
            }}>
              {error}
            </div>
          )}

          {/* ── LOGIN FORM ── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Username" value={username} onChange={v => { setUsername(v); setError(''); setSuccess(''); }} placeholder="admin" autoFocus />
              <Field label="Password" type="password" value={password} onChange={v => { setPassword(v); setError(''); setSuccess(''); }} placeholder="••••••••" />

              <button
                type="submit"
                disabled={isBusy}
                style={{
                  marginTop: 8,
                  background: isBusy ? 'var(--border)' : 'linear-gradient(135deg, var(--accent-blue), var(--accent-blue-dim))',
                  border: 'none',
                  borderRadius: 'var(--radius)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  padding: '11px 0',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'opacity .15s',
                }}
              >
                {isBusy ? <><IconLoader /> Memproses…</> : 'Masuk'}
              </button>
            </form>
          )}

          {/* ── SETUP FORM ── */}
          {mode === 'setup' && (
            <form onSubmit={handleSetup} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Nama Lengkap" value={sFullname} onChange={v => { setSFullname(v); setError(''); }} placeholder="Anwar Admin" autoFocus />
              <Field label="Email" type="email" value={sEmail} onChange={v => { setSEmail(v); setError(''); }} placeholder="admin@example.com" />
              <Field label="Username" value={sUsername} onChange={v => { setSUsername(v); setError(''); }} placeholder="admin" />
              <Field label="Password" type="password" value={sPassword} onChange={v => { setSPassword(v); setError(''); }} placeholder="Min. 8 karakter" />
              <Field label="Konfirmasi Password" type="password" value={sPasswordConfirm} onChange={v => { setSPasswordConfirm(v); setError(''); }} placeholder="Ulangi password" />

              <button
                type="submit"
                disabled={isBusy}
                style={{
                  marginTop: 8,
                  background: isBusy ? 'var(--border)' : 'linear-gradient(135deg, var(--accent-green), #059669)',
                  border: 'none',
                  borderRadius: 'var(--radius)',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 600,
                  padding: '11px 0',
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                {isBusy ? <><IconLoader /> Memproses…</> : 'Buat Akun Admin'}
              </button>

              <button
                type="button"
                onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
                style={{
                  background: 'none', border: 'none',
                  color: 'var(--text-secondary)', fontSize: 13,
                  textDecoration: 'underline', cursor: 'pointer',
                  padding: 0, textAlign: 'center',
                }}
              >
                ← Kembali ke login
              </button>
            </form>
          )}
        </div>

        {/* footer */}
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
          RADIUS Management · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
